/**
 * Mascot — three figurines from the Nipagesh logo, reusable as a brand element.
 * One <Figure/> is the building block: head circle + dome body, navy outline.
 * Variants combine multiple Figures into scenes (welcome) or single emotional figures (empty/success).
 */
import type { ReactNode } from "react";

type FigureColor = "yellow" | "purple" | "blue" | "red" | "green";

const FILL: Record<FigureColor, string> = {
  yellow: "#F59E0B",
  purple: "#7C3AED",
  blue: "#2563EB",
  red: "#EF4444",
  green: "#059669",
};

const OUTLINE = "#2B2F54";

/**
 * Single figure (head + dome body) rendered as a group at the origin (0,0).
 * The figure occupies roughly x∈[-30, 30], y∈[0, 100] (head top at y=0, body bottom ~y=100).
 * Accessory may be drawn on top (e.g., mug, book, dumbbell).
 */
function Figure({
  color,
  accessory,
  transform,
}: {
  color: FigureColor;
  accessory?: ReactNode;
  transform?: string;
}) {
  const fill = FILL[color];
  return (
    <g transform={transform}>
      {/* outline pass — slightly larger silhouette in navy */}
      <circle cx="0" cy="22" r="24" fill={OUTLINE} />
      <path
        d="M -32 102 a 32 50 0 0 1 64 0 z"
        fill={OUTLINE}
        transform="translate(0 -8)"
      />
      {/* fill pass */}
      <circle cx="0" cy="22" r="20" fill={fill} />
      <path d="M -28 92 a 28 42 0 0 1 56 0 z" fill={fill} transform="translate(0 -4)" />
      {accessory}
    </g>
  );
}

/** Welcome hero: three figures with different accessories (coffee mug / running / boardgame box).
 *  ~280px wide × 200px tall when rendered at width=280. */
export function MascotScene({ width = 280 }: { width?: number }) {
  return (
    <svg
      viewBox="-160 -10 320 200"
      width={width}
      height={(width * 200) / 320}
      className="select-none"
      role="img"
      aria-label="Три персонажа Nipagesh"
    >
      {/* soft pastel ground shadow under figures */}
      <ellipse cx="-90" cy="170" rx="55" ry="6" fill="#000" opacity="0.05" />
      <ellipse cx="0" cy="170" rx="55" ry="6" fill="#000" opacity="0.05" />
      <ellipse cx="90" cy="170" rx="55" ry="6" fill="#000" opacity="0.05" />

      {/* left — yellow with coffee mug */}
      <Figure
        color="yellow"
        transform="translate(-90 50)"
        accessory={
          <g transform="translate(20 60)">
            {/* mug body */}
            <rect x="-10" y="-2" width="20" height="22" rx="3" fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2.5" />
            {/* mug handle */}
            <path d="M 10 4 q 6 0 6 6 q 0 6 -6 6" fill="none" stroke={OUTLINE} strokeWidth="2.5" />
            {/* steam */}
            <path d="M -4 -6 q -2 -4 0 -8" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
            <path d="M 2 -6 q 2 -4 0 -8" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          </g>
        }
      />

      {/* center — purple "in motion" leaning slightly with arms-up vibe */}
      <Figure
        color="purple"
        transform="translate(0 40) scale(1.05)"
        accessory={
          // raised arms (cheering)
          <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
            <path d="M -22 58 q -10 -15 -6 -32" />
            <path d="M 22 58 q 10 -15 6 -32" />
            <circle cx="-28" cy="25" r="4" fill={FILL.purple} stroke={OUTLINE} strokeWidth="2" />
            <circle cx="28" cy="25" r="4" fill={FILL.purple} stroke={OUTLINE} strokeWidth="2" />
          </g>
        }
      />

      {/* right — blue with boardgame box */}
      <Figure
        color="blue"
        transform="translate(90 50)"
        accessory={
          <g transform="translate(-20 65) rotate(-8)">
            {/* box */}
            <rect x="-14" y="0" width="28" height="20" rx="3" fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2.5" />
            {/* dice dots */}
            <circle cx="-7" cy="7" r="1.8" fill={OUTLINE} />
            <circle cx="0" cy="10" r="1.8" fill={OUTLINE} />
            <circle cx="7" cy="13" r="1.8" fill={OUTLINE} />
          </g>
        }
      />
    </svg>
  );
}

/** Empty state: one pensive figure (head tilted, no body accessory, hand to chin).
 *  ~120px square when default. */
export function MascotPensive({ size = 120 }: { size?: number }) {
  // viewBox extended on the top-left so the third (largest) thought bubble
  // isn't clipped. The bubble sits at (-50, -10) r=6 and the parent's -6°
  // rotation drifts it further toward (-57, -4); the previous box
  // (-60 -10 120 130) cut it off on both the left and the top.
  return (
    <svg
      viewBox="-70 -25 145 150"
      width={size}
      height={size}
      className="select-none"
      role="img"
      aria-label="Задумчивый персонаж"
    >
      <ellipse cx="0" cy="115" rx="40" ry="4" fill="#000" opacity="0.05" />
      <g transform="rotate(-6 0 60)">
        <Figure color="purple" />
        {/* hand to chin */}
        <g stroke={OUTLINE} strokeWidth="2.5" strokeLinecap="round" fill={FILL.purple}>
          <path d="M -10 55 q -8 -8 -2 -18" fill="none" />
          <circle cx="-12" cy="34" r="4" />
        </g>
        {/* thought bubble */}
        <g fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2">
          <circle cx="-32" cy="12" r="3" />
          <circle cx="-40" cy="0" r="4" />
          <circle cx="-50" cy="-10" r="6" />
        </g>
      </g>
    </svg>
  );
}

/** Success state: one cheering figure (arms up, slight bounce). */
export function MascotCheer({ size = 120 }: { size?: number }) {
  return (
    <svg
      viewBox="-60 -10 120 130"
      width={size}
      height={size}
      className="select-none"
      role="img"
      aria-label="Празднующий персонаж"
    >
      <ellipse cx="0" cy="115" rx="40" ry="4" fill="#000" opacity="0.05" />
      <Figure
        color="yellow"
        accessory={
          <g stroke={OUTLINE} strokeWidth="2.5" strokeLinecap="round" fill="none">
            <path d="M -22 58 q -14 -18 -10 -38" />
            <path d="M 22 58 q 14 -18 10 -38" />
            <circle cx="-32" cy="22" r="4" fill={FILL.yellow} stroke={OUTLINE} strokeWidth="2" />
            <circle cx="32" cy="22" r="4" fill={FILL.yellow} stroke={OUTLINE} strokeWidth="2" />
          </g>
        }
      />
      {/* sparkles */}
      <g fill={FILL.yellow}>
        <circle cx="-40" cy="20" r="2.5" />
        <circle cx="42" cy="14" r="3" />
        <circle cx="-50" cy="50" r="2" />
        <circle cx="48" cy="48" r="2" />
      </g>
    </svg>
  );
}

/** Compact mascot logo for tab bar / inline — single figure, no accessories. */
export function MascotMark({ size = 32, color = "purple" }: { size?: number; color?: FigureColor }) {
  return (
    <svg viewBox="-35 -5 70 110" width={size} height={size} className="select-none">
      <Figure color={color} />
    </svg>
  );
}

/** Single walking figure with a leg-bounce CSS animation. Used when a section
 *  is "the journey" — one mascot strolling across the section. */
export function MascotWalking({
  size = 120,
  color = "yellow",
}: {
  size?: number;
  color?: FigureColor;
}) {
  return (
    <svg
      viewBox="-60 -10 120 130"
      width={size}
      height={size}
      className="select-none nf-walk"
      role="img"
      aria-label="Идущий персонаж"
    >
      <ellipse cx="0" cy="115" rx="40" ry="4" fill="#000" opacity="0.05" />
      <Figure color={color} />
      {/* a small "arm" swinging to suggest movement */}
      <g stroke={OUTLINE} strokeWidth="2.5" strokeLinecap="round" fill="none">
        <path d="M -22 58 q -8 -6 -8 -16" />
        <path d="M 22 58 q 8 -6 8 -16" />
      </g>
    </svg>
  );
}

/** Two figures approaching each other, waving. Used in the "meet" section. */
export function MascotMeeting({ width = 280 }: { width?: number }) {
  return (
    <svg
      viewBox="-160 -10 320 200"
      width={width}
      height={(width * 200) / 320}
      className="select-none"
      role="img"
      aria-label="Двое здороваются"
    >
      <ellipse cx="-70" cy="170" rx="45" ry="5" fill="#000" opacity="0.05" />
      <ellipse cx="70" cy="170" rx="45" ry="5" fill="#000" opacity="0.05" />
      {/* Left figure waving with right hand */}
      <g className="nf-meet-left">
        <Figure
          color="yellow"
          transform="translate(-70 50)"
          accessory={
            <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
              <path d="M 22 58 q 14 -18 14 -38" />
              <circle cx="36" cy="20" r="4" fill={FILL.yellow} stroke={OUTLINE} strokeWidth="2" />
            </g>
          }
        />
      </g>
      {/* Right figure waving with left hand */}
      <g className="nf-meet-right">
        <Figure
          color="blue"
          transform="translate(70 50) scale(-1 1)"
          accessory={
            <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
              <path d="M 22 58 q 14 -18 14 -38" />
              <circle cx="36" cy="20" r="4" fill={FILL.blue} stroke={OUTLINE} strokeWidth="2" />
            </g>
          }
        />
      </g>
    </svg>
  );
}

/** Two figures sitting close together with speech bubbles — the "conversation" section. */
export function MascotTalking({ width = 280 }: { width?: number }) {
  return (
    <svg
      viewBox="-160 -10 320 220"
      width={width}
      height={(width * 220) / 320}
      className="select-none"
      role="img"
      aria-label="Двое разговаривают"
    >
      <ellipse cx="0" cy="190" rx="120" ry="8" fill="#000" opacity="0.05" />
      {/* Left figure — sitting */}
      <Figure color="purple" transform="translate(-50 80) scale(0.9)" />
      {/* Right figure — sitting, mirrored */}
      <Figure color="yellow" transform="translate(50 80) scale(0.9)" />

      {/* Speech bubble — left figure */}
      <g className="nf-bubble nf-bubble-1">
        <path
          d="M -100 30 q 0 -16 18 -16 h 36 q 18 0 18 16 v 12 q 0 16 -18 16 h -22 l -10 10 v -10 h -4 q -18 0 -18 -16 z"
          fill="#FFFFFF"
          stroke={OUTLINE}
          strokeWidth="2"
        />
        <circle cx="-72" cy="34" r="2.5" fill={OUTLINE} />
        <circle cx="-64" cy="34" r="2.5" fill={OUTLINE} />
        <circle cx="-56" cy="34" r="2.5" fill={OUTLINE} />
      </g>

      {/* Speech bubble — right figure */}
      <g className="nf-bubble nf-bubble-2">
        <path
          d="M 30 50 q 0 -16 18 -16 h 36 q 18 0 18 16 v 12 q 0 16 -18 16 h -22 l -10 10 v -10 h -4 q -18 0 -18 -16 z"
          fill="#FFFFFF"
          stroke={OUTLINE}
          strokeWidth="2"
        />
        <circle cx="58" cy="54" r="2.5" fill={OUTLINE} />
        <circle cx="66" cy="54" r="2.5" fill={OUTLINE} />
        <circle cx="74" cy="54" r="2.5" fill={OUTLINE} />
      </g>
    </svg>
  );
}

/** Tiny figure (~60px tall) for the sticky scroll mascot. Plain body, no arms. */
export function MascotMini({
  size = 60,
  color = "purple",
}: {
  size?: number;
  color?: FigureColor;
}) {
  return (
    <svg viewBox="-35 -5 70 115" width={size * 0.6} height={size} className="select-none">
      <ellipse cx="0" cy="108" rx="22" ry="3" fill="#000" opacity="0.12" />
      <Figure color={color} />
    </svg>
  );
}

/** Tiny figure with a coffee mug in one hand — used as the "hero" sticky mascot. */
export function MascotMiniCoffee({
  size = 60,
  color = "yellow",
}: {
  size?: number;
  color?: FigureColor;
}) {
  return (
    <svg viewBox="-35 -5 70 115" width={size * 0.6} height={size} className="select-none">
      <ellipse cx="0" cy="108" rx="22" ry="3" fill="#000" opacity="0.12" />
      <Figure
        color={color}
        accessory={
          <g transform="translate(18 56)">
            <rect x="-7" y="-2" width="14" height="16" rx="2" fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2" />
            <path d="M 7 3 q 4 0 4 4 q 0 4 -4 4" fill="none" stroke={OUTLINE} strokeWidth="2" />
            <path d="M -3 -5 q -1.5 -3 0 -6" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M 1 -5 q 1.5 -3 0 -6" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
          </g>
        }
      />
    </svg>
  );
}

/** Single figure waving goodbye — the "farewell" section. */
export function MascotWaving({ size = 140, color = "blue" }: { size?: number; color?: FigureColor }) {
  return (
    <svg
      viewBox="-60 -10 120 130"
      width={size}
      height={size}
      className="select-none"
      role="img"
      aria-label="Машет рукой"
    >
      <ellipse cx="0" cy="115" rx="40" ry="4" fill="#000" opacity="0.05" />
      <Figure
        color={color}
        accessory={
          <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
            <path d="M -22 58 q -10 -15 -6 -32" />
            <g className="nf-wave-arm" style={{ transformOrigin: "22px 58px" }}>
              <path d="M 22 58 q 14 -18 14 -38" />
              <circle cx="36" cy="20" r="4" fill={FILL[color]} stroke={OUTLINE} strokeWidth="2" />
            </g>
          </g>
        }
      />
    </svg>
  );
}
